import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, GraduationCap, Users, Building2 } from 'lucide-react';

const highlights = [
  {
    icon: GraduationCap,
    title: 'For students',
    text: 'Discover strengths and find paths that fit who you are.',
  },
  {
    icon: Users,
    title: 'For parents',
    text: 'Replace guesswork with clear, evidence-based guidance.',
  },
  {
    icon: Building2,
    title: 'For institutions',
    text: 'Turn assessment data into decisions that scale.',
  },
];

const MainHero = () => {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#0a1628]">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
        style={{ backgroundImage: 'url(/images/home-banner.webp)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#1a1040]/90 to-[#2e1065]/55" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 85% 50%, rgba(139,92,246,0.38) 0%, transparent 65%), radial-gradient(ellipse 40% 40% at 10% 75%, rgba(124,58,237,0.2) 0%, transparent 60%)',
        }}
        aria-hidden
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="max-w-xl"
          >
            <p className="font-source text-[clamp(2.5rem,6vw,3.75rem)] text-white font-light tracking-tight leading-[1.05] mb-4">
              Analytical
              <span className="block italic text-violet-200">Prism</span>
            </p>

            <h1 className="font-source text-xl sm:text-2xl md:text-[1.75rem] text-white font-light leading-snug mb-4">
              From doubt to direction — assessments that reveal who you are and where you thrive.
            </h1>

            <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-7 font-sans max-w-lg">
              We combine rigorous psychometrics with practical guidance so students choose with
              confidence, parents support with clarity, and institutions act on real insight —
              not assumptions.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-9">
              <a
                href="/app/login"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-purple-500 hover:from-violet-500 hover:to-purple-400 transition-colors shadow-lg shadow-violet-900/30"
              >
                Start your assessment
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/solutions"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-medium text-white border border-purple-300/40 hover:bg-purple-500/15 transition-colors"
              >
                Explore solutions
              </a>
            </div>

            <div className="space-y-4 border-t border-white/10 pt-6">
              {highlights.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: 0.25 + index * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/20 text-violet-200">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.title}</p>
                    <p className="text-sm text-white/65 leading-relaxed">{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="relative flex flex-col items-center lg:items-end justify-center">
            <div className="relative flex justify-center lg:justify-end min-h-[240px] sm:min-h-[340px] lg:min-h-[460px] w-full">
              <motion.div
                className="pointer-events-none absolute top-1/2 left-1/2 h-[75%] w-[75%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
                style={{
                  background:
                    'radial-gradient(circle, rgba(192,132,252,0.4) 0%, rgba(139,92,246,0.25) 40%, transparent 70%)',
                }}
                animate={
                  reduceMotion
                    ? { opacity: 0.55 }
                    : { opacity: [0.4, 0.75, 0.4], scale: [1, 1.08, 1] }
                }
                transition={
                  reduceMotion
                    ? undefined
                    : { duration: 4.5, repeat: Infinity, ease: 'easeInOut' }
                }
                aria-hidden
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.92, x: 20 }}
                animate={
                  reduceMotion
                    ? { opacity: 1, scale: 1, x: 0, y: 0 }
                    : {
                        opacity: 1,
                        scale: [1, 1.04, 1],
                        x: 0,
                        y: [0, -14, 0],
                        rotate: [0, 1.5, 0, -1.5, 0],
                      }
                }
                transition={
                  reduceMotion
                    ? { duration: 0.6 }
                    : {
                        opacity: { duration: 0.7 },
                        x: { duration: 0.7 },
                        scale: { duration: 5, repeat: Infinity, ease: 'easeInOut' },
                        y: { duration: 5, repeat: Infinity, ease: 'easeInOut' },
                        rotate: { duration: 10, repeat: Infinity, ease: 'easeInOut' },
                      }
                }
                className="relative z-10 w-[min(100%,28rem)] sm:w-[min(100%,34rem)] lg:w-[min(100%,42rem)] xl:w-[46rem]"
              >
                <picture>
                  <source
                    srcSet="/images/pyramid_prism-colored.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/pyramid_prism-colored.png"
                    alt="Analytical Prism"
                    width={1400}
                    height={691}
                    decoding="async"
                    fetchPriority="high"
                    className="w-full h-auto object-contain drop-shadow-2xl select-none"
                    draggable={false}
                  />
                </picture>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative z-10 mt-4 lg:mt-2 text-center lg:text-right max-w-md lg:max-w-lg px-2"
            >
              <p className="inline-flex items-center justify-center lg:justify-end gap-2 text-violet-200/90 text-xs sm:text-sm font-medium tracking-wide mb-2">
                <Sparkles className="w-4 h-4 text-violet-300 shrink-0" />
                Science-backed self-awareness for Indian learners
              </p>
              <p className="text-xs sm:text-sm text-violet-200/70 tracking-wide font-sans">
                Trusted by institutions nationwide · 15+ psychometric domains · Personalised reports
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MainHero;
